import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Input, Button, Space, Modal, Select, Card,
  Tag, notification, Dropdown, Menu, DatePicker,
  Segmented, Spin, Alert, Row, Col, Tooltip,
  Statistic,
  Tabs,
  Switch,
  Badge
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DownloadOutlined,
  EyeOutlined, CheckCircleOutlined, CloseCircleOutlined,
  DollarOutlined, ExclamationCircleOutlined, UserOutlined,
  CalendarOutlined, MoreOutlined, WarningOutlined,
  TableOutlined, AppstoreOutlined, HomeOutlined,
  ShoppingCartOutlined, CreditCardOutlined
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import ordersService from '../../../services/ordersService';
import paymentService from '../../../services/paymentService';
import userService from '../../../services/userService';
import DateRangeSelector from '../../../components/DateRangeSelector';
import EnhancedSearchComponent from '../../../components/EnhancedSearchComponent';
import { formatPrice, formatNumber, extractTimeFromOrder } from '../../../utils/orderUtils';
import ImpersonationBanner from '../Users/ImpersonationBanner';
import dayjs from 'dayjs';
import userRoleUtils from '../../../utils/userRoleUtils';
import MyTable from '../../../components/common/MyTable';
import ClientOptionLabel, { buildClientSearchText, getClientSelectedLabel } from '../../../components/common/ClientOptionLabel';
import ViewModal from './modals/ViewModal';
import {
  canCaptureSavedCard as isSavedCardCaptureEligible,
  getSavedCardActionLabel,
  getSavedCardActionTooltip,
  getSavedCardConfirmationCopy,
  isStripePayLaterOrder,
} from '../../../utils/orderPaymentAction';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

/**
 * Payment Management Component
 * Handles viewing and updating payment statuses for orders
 */
const SimplifiedPaymentManagement = () => {
  const location = useLocation();
  const [viewOrderId, setViewOrderId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [metrics, setMetrics] = useState({
    totalPayments: 0,
    paidAmount: 0,
    unpaidAmount: 0,
    refundedAmount: 0
  });
  
  // Client selection states
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  
  // Check if user is admin or impersonating
  const isAdmin = userRoleUtils.isAdmin();
  const isImpersonating = userRoleUtils.isImpersonating();
  
  // Date range states
  const today = dayjs().format('YYYY-MM-DD');
  const [dateViewMode, setDateViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateDisplayText, setDateDisplayText] = useState(dayjs().format('DD MMM YYYY'));
  const [isCustomRange, setIsCustomRange] = useState(false);
  
  // View type (table or card)
  const [viewType, setViewType] = useState(isAdmin ? 'table' : 'card');
  // Group orders by date
  const [groupByDate, setGroupByDate] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    date_range: [today, today],
    order_status: null,
    payment_status: activeTab === 'all' ? null : getPaymentStatusValue(activeTab),
    client_id: null
  });
  
  // Pagination & Sorting
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [sortInfo, setSortInfo] = useState({
    columnKey: 'created_at',
    order: 'descend'
  });

  // Bulk action states
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionVisible, setBulkActionVisible] = useState(false);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  
  const searchInputRef = useRef(null);
  const ordersRequestRef = useRef(0);
  
  // Load orders and clients on component mount
  useEffect(() => {
    // Set initial date display text
    setDateDisplayText(dayjs(today).format('DD MMM YYYY'));
    fetchClients();
  }, []);
  
  // Fetch orders whenever filters or pagination change
  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.current, pagination.pageSize, sortInfo, activeTab, searchText, searchCategory, selectedClient]);
  
  // Function to convert tab names to payment status values
  function getPaymentStatusValue(tabName) {
    switch (tabName) {
      case 'unpaid': return 0;
      case 'paid': return 1;
      case 'refunded': return 2;
      case 'partial_refund': return 3;
      default: return null;
    }
  }
  
  // Function to fetch clients/users
  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      
      // Fetch users from the API
      const response = await userService.getAllCustomers({
        page: 1,
        limit: 1000, // Get a large number to include all users
        sort_field: 'first_name',
        sort_order: 'asc'
      });
      
      if (response && response.data) {
        // Keep raw customer objects so we can render company/contact lines consistently
        setClients(response.data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      notification.error({
        message: 'Failed to load clients',
        description: 'Unable to fetch client list for filtering'
      });
    } finally {
      setClientsLoading(false);
    }
  };
  
  // Function to fetch orders with payment status
  const fetchOrders = async () => {
    const requestId = ++ordersRequestRef.current;
    try {
      setLoading(true);
      
      // Update filters based on active tab
      const updatedFilters = {
        ...filters,
        payment_status: activeTab === 'all' ? filters.payment_status : getPaymentStatusValue(activeTab)
      };
      
      // Create params object for API request
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        sort_field: sortInfo.columnKey,
        sort_order: sortInfo.order === 'ascend' ? 'asc' : 'desc',
        order_status: updatedFilters.order_status,
        payment_status: updatedFilters.payment_status,
        user_id: selectedClient || updatedFilters.client_id,
        calculate_metrics: true
      };
      
      // Add search parameters
      if (searchText) {
        if (searchCategory === 'all') {
          params.search = searchText;
        } else if (searchCategory === 'property') {
          params.search_property = searchText;
        } else if (searchCategory === 'address') {
          params.search_address = searchText;
        } else if (searchCategory === 'postcode') {
          params.search_postcode = searchText;
        } else if (searchCategory === 'customer') {
          params.search_customer = searchText;
        }
      }
      
      // Add date range params
      if (updatedFilters.date_range && updatedFilters.date_range.length === 2) {
        // If it's the same date (single day selection), use property_date
        if (updatedFilters.date_range[0] === updatedFilters.date_range[1]) {
          params.property_date = updatedFilters.date_range[0];
        } else {
          // Otherwise use start_date and end_date for a range
          params.start_date = updatedFilters.date_range[0];
          params.end_date = updatedFilters.date_range[1];
        }
      }
      
      // Fetch orders with filters
      const response = await ordersService.getAllOrdersAdmin(params);
      
      if (response && requestId === ordersRequestRef.current) {
        setOrders(response.data || response.orders || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0
        }));
        
        // Update metrics if available
        if (response.metrics) {
          setMetrics({
            totalPayments: response.metrics.total_orders || response.metrics.total_count || 0,
            paidAmount: response.metrics.paid_amount || 0,
            unpaidAmount: response.metrics.unpaid_amount || 0,
            refundedAmount: response.metrics.refunded_amount || 0
          });
        }
      }
    } catch (error) {
      if (requestId !== ordersRequestRef.current) return;
      console.error('Error fetching orders:', error);
      notification.error({
        message: 'Failed to load orders',
        description: error.message || 'An error occurred while loading orders'
      });
    } finally {
      if (requestId === ordersRequestRef.current) setLoading(false);
    }
  };
  
  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (pagination, tableFilters, sorter) => {
    setPagination(prev => ({
      ...prev,
      ...pagination
    }));

    const filterPatch = {};

    if (tableFilters && Object.prototype.hasOwnProperty.call(tableFilters, 'order_status')) {
      filterPatch.order_status = tableFilters.order_status?.length ? tableFilters.order_status[0] : null;
    }

    if (tableFilters && Object.prototype.hasOwnProperty.call(tableFilters, 'payment_status')) {
      filterPatch.payment_status = tableFilters.payment_status?.length ? tableFilters.payment_status[0] : null;
      setActiveTab('all');
    }

    if (Object.keys(filterPatch).length > 0) {
      setFilters(prev => ({
        ...prev,
        ...filterPatch
      }));
    }
    
    // Update sortInfo if sorter has column key
    if (sorter && sorter.columnKey) {
      setSortInfo({
        columnKey: sorter.columnKey,
        order: sorter.order
      });
    } else {
      setSortInfo({ columnKey: 'created_at', order: 'descend' });
    }
  };
  
  // Handle search input change
  const handleSearch = (value, category = searchCategory) => {
    setSearchText(value);
    setSearchCategory(category);
    setPagination({ ...pagination, current: 1 });
  };
  
  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, current: 1 });
  };
  
  // Handle client selection change
  const handleClientChange = (clientId) => {
    console.log('Selected client:', clientId);
    setSelectedClient(clientId);
    setFilters(prev => ({ ...prev, client_id: clientId }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    const todayFormatted = dayjs().format('YYYY-MM-DD');
    
    setFilters({
      order_status: null,
      payment_status: null,
      date_range: [todayFormatted, todayFormatted],
      client_id: null
    });
    setDateViewMode('today');
    setCurrentDate(new Date());
    setDateDisplayText(dayjs().format('DD MMM YYYY'));
    setIsCustomRange(false);
    setSearchText('');
    setSearchCategory('all');
    setActiveTab('all');
    setSelectedClient(null);
    setSortInfo({ columnKey: 'created_at', order: 'descend' });
    setPagination({ ...pagination, current: 1 });
    
    if (searchInputRef.current) {
      searchInputRef.current.input.value = '';
    }
    
    fetchOrders();
  };
  
  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);
    setFilters(prev => ({
      ...prev,
      payment_status: key === 'all' ? null : getPaymentStatusValue(key)
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };
  
  // Handle date range changes from DateRangeSelector
  const handleDateRangeChange = (dateRange, viewMode, displayText, newDate) => {
    if (!dateRange || dateRange.length !== 2) return;
    
    // Update current date state if provided, otherwise use the first date of the range
    if (newDate) {
      setCurrentDate(newDate);
    } else if (viewMode !== 'custom') {
      setCurrentDate(new Date(dateRange[0]));
    }
    
    // Update date view mode
    setDateViewMode(viewMode);
    
    // Update display text
    setDateDisplayText(displayText);
    
    // Update filters
    setFilters(prev => ({
      ...prev,
      date_range: dateRange
    }));
    
    // Reset pagination to first page when changing date filter
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };
  
  // Handle custom range flag
  const handleCustomRangeChange = (isCustom, dateRange) => {
    setIsCustomRange(isCustom);
    
    if (dateRange && dateRange.length === 2) {
      setFilters(prev => ({
        ...prev,
        date_range: dateRange
      }));
    }
  };
  
  // Handle date search from EnhancedSearchComponent
  const handleDateSearchChange = (startDate, endDate) => {
    if (startDate && endDate) {
      const dateRange = [startDate, endDate];
      const displayText = `${dayjs(startDate).format('DD MMM YYYY')} - ${dayjs(endDate).format('DD MMM YYYY')}`;
      
      setFilters(prev => ({
        ...prev,
        date_range: dateRange
      }));
      
      setIsCustomRange(true);
      setDateViewMode('custom');
      setDateDisplayText(displayText);
      setPagination(prev => ({
        ...prev,
        current: 1
      }));
    }
  };
  
  // Handle view order details
  const handleViewOrder = (orderId) => {
    setViewOrderId(orderId);
  };
  
  // Handle Export
  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Create export params
      const exportParams = {
        search: searchText,
        order_status: filters.order_status,
        payment_status: filters.payment_status,
        user_id: filters.client_id,
        start_date: filters.date_range[0],
        end_date: filters.date_range[1],
        sort_field: sortInfo.columnKey,
        sort_order: sortInfo.order === 'ascend' ? 'asc' : 'desc'
      };
      
      await ordersService.exportOrders(exportParams);
      
      notification.success({
        message: 'Export Started',
        description: 'Your export has been initiated. You will be notified when it is ready for download.'
      });
    } catch (error) {
      notification.error({
        message: 'Export Failed',
        description: error.message || 'An error occurred while exporting orders'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ====== PAYMENT STATUS MANAGEMENT FUNCTIONS ======
  
  // Handle marking order as paid
  const handleMarkAsPaid = async (orderId) => {
    try {
      setLoading(true);
      
      // Call payment service to mark order as paid
      await paymentService.markAsPaid(orderId);
      
      notification.success({
        message: 'Order Marked as Paid',
        description: 'The payment status has been updated successfully.'
      });
      
      // Update the order in the list
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, payment_status: 1 } 
            : order
        )
      );
      
      // Refresh orders after a short delay
      setTimeout(fetchOrders, 500);
    } catch (error) {
      console.error('Error marking order as paid:', error);
      notification.error({
        message: 'Failed to update payment status',
        description: error.message || 'An error occurred while updating payment status'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCaptureSavedCard = async (orderId, order = {}) => {
    const confirmationCopy = getSavedCardConfirmationCopy(order);
    return new Promise((resolve) => confirm({
      title: confirmationCopy.title,
      icon: <CreditCardOutlined />,
      content: confirmationCopy.content,
      onOk: async () => {
        try {
          setLoading(true);
          await paymentService.captureSavedCard(orderId);
          notification.success({
            message: 'Payment Captured',
            description: 'The saved card was charged successfully.'
          });
          setTimeout(fetchOrders, 500);
        } catch (error) {
          notification.error({
            message: 'Payment capture failed',
            description: error.message || 'Unable to charge the saved card.'
          });
        } finally {
          setLoading(false);
          resolve();
        }
      },
      onCancel: () => resolve()
    }));
  };
  
  // Handle marking order as unpaid
  const handleMarkAsUnpaid = async (orderId) => {
    confirm({
      title: 'Mark as Unpaid',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to mark this order as unpaid?',
      onOk: async () => {
        try {
          setLoading(true);
          
          await paymentService.markAsUnpaid(orderId);
          
          notification.success({
            message: 'Order Marked as Unpaid',
            description: 'The payment status has been updated successfully.'
          });
          
          // Update the order in the list
          setOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === orderId 
                ? { ...order, payment_status: 0 } 
                : order
            )
          );
          
          // Refresh orders after a short delay
          setTimeout(fetchOrders, 500);
        } catch (error) {
          console.error('Error marking order as unpaid:', error);
          notification.error({
            message: 'Failed to update payment status',
            description: error.message || 'An error occurred while updating payment status'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  // Handle marking order as refunded
  const handleMarkAsRefunded = async (orderId) => {
    confirm({
      title: 'Mark as Refunded',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to mark this order as refunded?',
      onOk: async () => {
        try {
          setLoading(true);
          
          await paymentService.markAsRefunded(orderId);
          
          notification.success({
            message: 'Order Marked as Refunded',
            description: 'The payment status has been updated successfully.'
          });
          
          // Update the order in the list
          setOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === orderId 
                ? { ...order, payment_status: 2 } 
                : order
            )
          );
          
          // Refresh orders after a short delay
          setTimeout(fetchOrders, 500);
        } catch (error) {
          console.error('Error marking order as refunded:', error);
          notification.error({
            message: 'Failed to update payment status',
            description: error.message || 'An error occurred while updating payment status'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  // Handle marking order as partially refunded
  const handleMarkAsPartialRefund = async (orderId) => {
    // Open modal to enter partial refund amount
    Modal.confirm({
      title: 'Enter Partial Refund Amount',
      icon: <DollarOutlined />,
      content: (
        <div>
          <p>Please enter the refund amount:</p>
          <Input 
            prefix="£" 
            placeholder="0.00"
            type="number"
            id="refund-amount-input"
            min="0.01"
            step="0.01"
          />
        </div>
      ),
      onOk: async () => {
        try {
          // Get refund amount from input
          const refundAmountInput = document.getElementById('refund-amount-input');
          const refundAmount = parseFloat(refundAmountInput.value);
          
          if (isNaN(refundAmount) || refundAmount <= 0) {
            notification.error({
              message: 'Invalid Amount',
              description: 'Please enter a valid refund amount greater than zero.'
            });
            return Promise.reject('Invalid amount');
          }
          
          setLoading(true);
          
          // Call payment service to mark as partially refunded
          await paymentService.markAsPartialRefund(orderId, refundAmount);
          
          notification.success({
            message: 'Partial Refund Processed',
            description: `Refund of £${refundAmount.toFixed(2)} has been processed.`
          });
          
          // Update the order in the list
          setOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === orderId 
                ? { ...order, payment_status: 3, refund_amount: refundAmount } 
                : order
            )
          );
          
          // Refresh orders after a short delay
          setTimeout(fetchOrders, 500);
          
          return Promise.resolve();
        } catch (error) {
          console.error('Error processing partial refund:', error);
          notification.error({
            message: 'Failed to process refund',
            description: error.message || 'An error occurred while processing the refund'
          });
          return Promise.reject(error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Handle bulk payment status change
  const handleBulkPaymentStatusChange = () => {
    if (!selectedPaymentStatus || selectedRowKeys.length === 0) {
      notification.warning({
        message: 'Please select a payment status and at least one order'
      });
      return;
    }

    let statusText = '';
    switch (selectedPaymentStatus) {
      case '0': statusText = 'Unpaid'; break;
      case '1': statusText = 'Paid'; break;
      case '2': statusText = 'Refunded'; break;
      case '3': statusText = 'Partially Refunded'; break;
      default: statusText = 'Unknown';
    }

    confirm({
      title: `Change ${selectedRowKeys.length} orders to "${statusText}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This will update the payment status for all selected orders.',
      onOk: async () => {
        try {
          setLoading(true);
          
          // If partial refund, prompt for amount
          if (selectedPaymentStatus === '3') {
            Modal.confirm({
              title: 'Enter Partial Refund Amount',
              icon: <DollarOutlined />,
              content: (
                <div>
                  <p>Please enter the refund amount for all selected orders:</p>
                  <Input 
                    prefix="£" 
                    placeholder="0.00"
                    type="number"
                    id="bulk-refund-amount-input"
                    min="0.01"
                    step="0.01"
                  />
                  <p className="text-orange-500 mt-2">
                    <WarningOutlined /> This amount will be applied to all selected orders.
                  </p>
                </div>
              ),
              onOk: async () => {
                const refundAmountInput = document.getElementById('bulk-refund-amount-input');
                const refundAmount = parseFloat(refundAmountInput.value);
                
                if (isNaN(refundAmount) || refundAmount <= 0) {
                  notification.error({
                    message: 'Invalid Amount',
                    description: 'Please enter a valid refund amount greater than zero.'
                  });
                  return Promise.reject('Invalid amount');
                }
                
                await processBulkStatusChange(selectedPaymentStatus, refundAmount);
                return Promise.resolve();
              }
            });
          } else {
            // Process regular status change
            await processBulkStatusChange(selectedPaymentStatus);
          }
        } catch (error) {
          notification.error({
            message: 'Failed to update payment status',
            description: error.message || 'An error occurred while updating payment status'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleBulkMarkAsPaid = () => {
    if (selectedRowKeys.length === 0) {
      notification.warning({
        message: 'Please select at least one order'
      });
      return;
    }

    confirm({
      title: `Mark ${selectedRowKeys.length} selected orders as paid?`,
      icon: <CheckCircleOutlined />,
      content: 'This will update the payment status for all selected orders.',
      onOk: async () => {
        try {
          setLoading(true);
          await processBulkStatusChange('1');
        } catch (error) {
          notification.error({
            message: 'Failed to mark selected orders as paid',
            description: error.message || 'An error occurred while updating payment status'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  // Process bulk status change
  const processBulkStatusChange = async (status, refundAmount = null) => {
    try {
      // Call bulk update API
      const response = await paymentService.updateBulkPaymentStatus(selectedRowKeys, status, refundAmount);

      if (response && response.status === false) {
        throw new Error(response.message || 'Payment status update failed');
      }
      
      notification.success({
        message: 'Payment Status Updated',
        description: `Successfully updated ${selectedRowKeys.length} orders.`
      });
      
      // Reset selection
      setSelectedRowKeys([]);
      setSelectedPaymentStatus('');
      setBulkActionVisible(false);
      
      // Refresh orders
      fetchOrders();
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  };
  
  // Function to get payment status tag
  const getPaymentStatusTag = (status) => {
    switch (parseInt(status)) {
      case 0:
        return <Tag color="orange">Unpaid</Tag>;
      case 1:
        return <Tag color="green">Paid</Tag>;
      case 2:
        return <Tag color="red">Refunded</Tag>;
      case 3:
        return <Tag color="purple">Partial Refund</Tag>;
      default:
        return <Tag color="default">Unknown</Tag>;
    }
  };
  
  // Function to get order status tag
  const getOrderStatusTag = (status) => {
    switch (status) {
      case 'Scheduled':
        return <Tag color="blue">Scheduled</Tag>;
      case 'On-Route':
      case 'En-route':
        return <Tag color="orange">En-Route</Tag>;
      case 'Completed':
        return <Tag color="green">Completed</Tag>;
      case 'Collected':
        return <Tag color="purple">Collected</Tag>;
      case 'Cancelled':
        return <Tag color="red">Cancelled</Tag>;
      case 'Failed':
        return <Tag color="volcano">Failed</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };
  
  // Function to format currency
  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };
  
  // Render payment actions for a given order
  const renderPaymentActions = (record) => {
    const paymentStatus = parseInt(record.payment_status);
    const canCaptureSavedCard = isSavedCardCaptureEligible(record);
    const isStripePayLater = isStripePayLaterOrder(record);
    const chargeLabel = getSavedCardActionLabel(record);
    
    // Menu for payment actions
    const paymentMenu = (
      <Menu>
        <Menu.Item
          key="capture-card"
          icon={<CreditCardOutlined />}
          onClick={() => handleCaptureSavedCard(record.id, record)}
          disabled={!canCaptureSavedCard}
        >
          {chargeLabel}
        </Menu.Item>
        <Menu.Item
          key="paid" 
          icon={<CheckCircleOutlined />} 
          onClick={() => handleMarkAsPaid(record.id)}
          disabled={paymentStatus === 1}
        >
          Mark as Paid
        </Menu.Item>
        <Menu.Item 
          key="unpaid" 
          icon={<CloseCircleOutlined />} 
          onClick={() => handleMarkAsUnpaid(record.id)}
          disabled={paymentStatus === 0}
        >
          Mark as Unpaid
        </Menu.Item>
        <Menu.Item 
          key="refunded" 
          icon={<DollarOutlined />} 
          onClick={() => handleMarkAsRefunded(record.id)}
          disabled={paymentStatus === 2 || paymentStatus === 0}
        >
          Mark as Refunded
        </Menu.Item>
        <Menu.Item 
          key="partial" 
          icon={<DollarOutlined />} 
          onClick={() => handleMarkAsPartialRefund(record.id)}
          disabled={paymentStatus === 0}
        >
          Partial Refund
        </Menu.Item>
      </Menu>
    );
    
    // Quick action buttons based on current payment status
    const quickActions = () => {
      if (paymentStatus === 0) {
        return (
          <Space>
            {canCaptureSavedCard && (
              <Tooltip title={getSavedCardActionTooltip(record)}>
                <Button
                  type={isStripePayLater ? 'primary' : 'text'}
                  size="small"
                  icon={<CreditCardOutlined />}
                  aria-label={chargeLabel}
                  onClick={() => handleCaptureSavedCard(record.id, record)}
                  className={isStripePayLater ? '' : 'text-blue-500'}
                >
                  {isStripePayLater ? chargeLabel : null}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Mark as Paid">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => handleMarkAsPaid(record.id)}
                className="text-green-500"
              />
            </Tooltip>
          </Space>
        );
      } else if (paymentStatus === 1) {
        return (
          <Space>
            <Tooltip title="Mark as Refunded">
              <Button
                type="text"
                icon={<DollarOutlined />}
                onClick={() => handleMarkAsRefunded(record.id)}
                className="text-orange-500"
              />
            </Tooltip>
            <Tooltip title="Mark as Unpaid">
              <Button
                type="text"
                icon={<CloseCircleOutlined />}
                onClick={() => handleMarkAsUnpaid(record.id)}
                className="text-red-500"
              />
            </Tooltip>
          </Space>
        );
      } else if (paymentStatus === 2 || paymentStatus === 3) {
        return (
          <Tooltip title="Mark as Unpaid">
            <Button
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={() => handleMarkAsUnpaid(record.id)}
              className="text-red-500"
            />
          </Tooltip>
        );
      }
    };
    
    return (
      <div className="flex items-center space-x-2">
        {quickActions()}
        <Dropdown overlay={paymentMenu} placement="bottomRight">
          <Button 
            type="text" 
            icon={<MoreOutlined />} 
            className="text-gray-500"
          />
        </Dropdown>
      </div>
    );
  };
  
  // Menu for export options
  const actionsMenu = (
    <Menu>
      <Menu.Item key="export" onClick={handleExport} icon={<DownloadOutlined />}>
        Export Orders
      </Menu.Item>
      <Menu.Item key="refresh" onClick={fetchOrders} icon={<ReloadOutlined />}>
        Refresh Data
      </Menu.Item>
    </Menu>
  );
  
  // Table columns configuration
  const tableColumns = [
    {
      title: 'Order #',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <span className="font-medium">#{id}</span>,
      sorter: true,
      width: 80
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <div>
          <div className="flex items-center">
            <UserOutlined className="mr-1 text-gray-400" />
            <span>{record.delivery_first_name || ''} {record.delivery_last_name || ''}</span>
          </div>
          {record.user?.email && (
            <div className="text-xs text-gray-500 truncate max-w-[140px]">
              {record.user.email || record.order_email}
            </div>
          )}
        </div>
      ),
      width: 140
    },
    {
      title: 'Property',
      key: 'property',
      render: (_, record) => (
        <div>
          {record.order_property && record.order_property.property_title ?
            record.order_property.property_title : 'N/A'}
          <div className="text-xs text-gray-500">
            {record.order_property && record.order_property.apartment_number ?
              record.order_property.apartment_number : ''}
          </div>
        </div>
      ),
      width: 120
    },
    {
      title: 'Date',
      dataIndex: 'property_date',
      key: 'property_date',
      render: (date) => {
        if (!date) return 'N/A';
        return (
          <div>
            {new Date(date).toLocaleDateString()}
          </div>
        );
      },
      sorter: true,
      width: 100
    },
    {
      title: 'Total Amount',
      dataIndex: 'order_total',
      key: 'order_total',
      render: (total) => formatCurrency(total),
      sorter: true,
      width: 100
    },
    {
      title: 'Order Status',
      dataIndex: 'order_status',
      key: 'order_status',
      render: (status) => getOrderStatusTag(status),
      filters: [
        { text: 'Scheduled', value: 'Scheduled' },
        { text: 'En-Route', value: 'En-route' },
        { text: 'Completed', value: 'Completed' },
        { text: 'Collected', value: 'Collected' },
        { text: 'Cancelled', value: 'Cancelled' },
        { text: 'Failed', value: 'Failed' }
      ],
      filterMultiple: false,
      width: 120
    },
    {
      title: 'Payment Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status, record) => {
        // Display refund amount if partially refunded
        if (parseInt(status) === 3 && record.refund_amount) {
          return (
            <div>
              {getPaymentStatusTag(status)}
              <div className="text-xs text-gray-500 mt-1">
                Refunded: {formatCurrency(record.refund_amount)}
              </div>
            </div>
          );
        }
        return getPaymentStatusTag(status);
      },
      filters: [
        { text: 'Unpaid', value: 0 },
        { text: 'Paid', value: 1 },
        { text: 'Refunded', value: 2 },
        { text: 'Partial Refund', value: 3 }
      ],
      filteredValue: filters.payment_status !== null && filters.payment_status !== undefined ? [filters.payment_status] : null,
      filterMultiple: false,
      width: 120
    },
    {
      title: 'Payment Actions',
      key: 'payment_actions',
      render: (_, record) => renderPaymentActions(record),
      width: 220
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Order">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewOrder(record.id)}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
        </Space>
      ),
      width: 80,
      fixed: 'right'
    }
  ];
  
  // Row selection for bulk operations
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
      setBulkActionVisible(keys.length > 0);
    }
  };
  
  // Group orders by date if groupByDate is enabled
  const getOrdersGroupedByDate = () => {
    if (!groupByDate) return null;
    
    const grouped = {};
    
    orders.forEach(order => {
      let dateKey = 'No Date';
      
      if (order.property_date) {
        dateKey = order.property_date;
      }
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(order);
    });
    
    // Sort dates in descending order
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'No Date') return 1;
        if (b === 'No Date') return -1;
        return new Date(b) - new Date(a);
      })
      .map(date => ({
        date,
        displayDate: date === 'No Date' ? 'No Date' : dayjs(date).format('dddd, MMMM D, YYYY'),
        orders: grouped[date]
      }));
  };
  
  // Render order card for card view
  const renderOrderCard = (order) => {
    const formatOrderId = (id) => {
      return `#${id.toString().padStart(6, '0')}`;
    };
    
    // Format address properly
    const formatAddress = () => {
      try {
        const propertyTitle = order.order_property?.property_title || 'N/A';
        const apartmentNumber = order.order_property?.apartment_number || '';
        const address = order.order_property?.apartment_address || order.delivery_address_1 || '';
        const city = order.delivery_city || order.order_property?.delivery_city || '';
        const zipCode = order.delivery_zip_code || order.order_property?.delivery_zip_code || '';
        
        const parts = [apartmentNumber, address, city, zipCode].filter(Boolean);
        return parts.join(', ');
      } catch (error) {
        return "Address unavailable";
      }
    };
    
    // Get items count
    const getItemsCount = () => {
      try {
        if (order.order_items) {
          return order.order_items;
        }
        
        if (order.order_products && Array.isArray(order.order_products)) {
          return order.order_products.length;
        }
        
        return 0;
      } catch (error) {
        return 0;
      }
    };
    
    // Status style mapping for order cards
    const STATUS_STYLES = {
      0: { bg: 'bg-orange-100', text: 'text-orange-800' }, // Unpaid
      1: { bg: 'bg-green-100', text: 'text-green-800' },   // Paid
      2: { bg: 'bg-red-100', text: 'text-red-800' },       // Refunded
      3: { bg: 'bg-purple-100', text: 'text-purple-800' }  // Partial Refund
    };
    
    // Get payment status display text
    const getPaymentStatusText = (status) => {
      switch (parseInt(status)) {
        case 0: return 'Unpaid';
        case 1: return 'Paid';
        case 2: return 'Refunded';
        case 3: return 'Partial Refund';
        default: return 'Unknown';
      }
    };
    
    // Render payment action buttons for card view
    const renderCardPaymentActions = () => {
      const paymentStatus = parseInt(order.payment_status);
      const canCaptureSavedCard = isSavedCardCaptureEligible(order);
      const chargeLabel = getSavedCardActionLabel(order);
      
      if (paymentStatus === 0) {
        return (
          <div className="flex space-x-2">
            {canCaptureSavedCard && (
              <Button
                size="small"
                icon={<CreditCardOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCaptureSavedCard(order.id, order);
                }}
                className="bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200"
              >
                {chargeLabel}
              </Button>
            )}
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsPaid(order.id);
              }}
              className="bg-green-500 hover:bg-green-600"
            >
              Mark as Paid
            </Button>
          </div>
        );
      } else if (paymentStatus === 1) {
        return (
          <div className="flex space-x-2">
            <Button 
              size="small" 
              icon={<DollarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsRefunded(order.id);
              }}
              className="bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200"
            >
              Refund
            </Button>
            <Button 
              size="small" 
              icon={<CloseCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsUnpaid(order.id);
              }}
              className="bg-red-100 text-red-800 border-red-300 hover:bg-red-200"
            >
              Mark Unpaid
            </Button>
          </div>
        );
      } else if (paymentStatus === 2 || paymentStatus === 3) {
        return (
          <Button 
            size="small" 
            icon={<CloseCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleMarkAsUnpaid(order.id);
            }}
            className="bg-red-100 text-red-800 border-red-300 hover:bg-red-200"
          >
            Mark Unpaid
          </Button>
        );
      }
      
      return null;
    };
    
    return (
      <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
        <div className="bg-blue-50 text-gray-900 border-b border-blue-100 p-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <div className="bg-white border border-gray-200 text-golden-navy p-2 rounded-full mr-3 shadow-sm">
                <HomeOutlined className="h-4 w-4" />
              </div>
              <h3 className="text-xl font-semibold">{formatOrderId(order.id)}</h3>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-bold text-base">TOTAL: £{formatPrice(order.order_total)}</span>
              <Button 
                type="text"
                onClick={() => handleViewOrder(order.id)}
                className="text-golden-navy hover:text-blue-700 p-0"
                icon={<EyeOutlined className="text-lg" />}
              />
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="flex-grow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-semibold text-lg">
                  {order.order_property && order.order_property.property_title ? 
                   order.order_property.property_title : 'N/A'} 
                </p>
                <p className="text-sm text-gray-600">{formatAddress()}</p>
              </div>
              <div className="flex flex-col items-end">
                <span className={`px-3 py-1 rounded-full text-xs ${
                  STATUS_STYLES[parseInt(order.payment_status)]?.bg || 'bg-gray-100'
                } ${STATUS_STYLES[parseInt(order.payment_status)]?.text || 'text-gray-800'}`}>
                  {getPaymentStatusText(order.payment_status)}
                </span>
                <span className="mt-2">
                  {getOrderStatusTag(order.order_status)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
            <div>
              <p><strong>Date:</strong> {order.property_date ? new Date(order.property_date).toLocaleDateString() : 'N/A'}</p>
              <p><strong>Customer:</strong> {order.delivery_first_name || ''} {order.delivery_last_name || ''}</p>
            </div>
            <div className="text-right">
              <p><strong>Items:</strong> {getItemsCount()}</p>
              <p><strong>Order Status:</strong> {order.order_status}</p>
            </div>
          </div>
          
          {/* Payment Actions Section */}
          <div className="mt-4 pt-3 border-t flex justify-end">
            {renderCardPaymentActions()}
          </div>
        </div>
      </div>
    );
  };
  
  // Render grouped order cards
  const renderGroupedOrderCards = () => {
    const groupedOrders = getOrdersGroupedByDate();
    
    return groupedOrders.map(group => (
      <div key={group.date} className="mb-8">
        <h2 className="text-xl font-bold mb-4 pb-2 border-b flex items-center">
          <CalendarOutlined className="mr-2" />
          {group.displayDate} ({group.orders.length})
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
          {group.orders.map(order => renderOrderCard(order))}
        </div>
      </div>
    ));
  };
  
  // Render payment metrics cards
  const renderPaymentMetrics = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="shadow-sm">
          <Statistic
            title="Total Orders"
            value={metrics.totalPayments || 0}
            prefix={<ShoppingCartOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
        <Card className="shadow-sm">
          <Statistic
            title="Paid Amount"
            value={`£${formatPrice(metrics.paidAmount)}`}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
        <Card className="shadow-sm">
          <Statistic
            title="Unpaid Amount"
            value={`£${formatPrice(metrics.unpaidAmount)}`}
            prefix={<CloseCircleOutlined />}
            valueStyle={{ color: '#faad14' }}
          />
        </Card>
        <Card className="shadow-sm">
          <Statistic
            title="Refunded Amount"
            value={`£${formatPrice(metrics.refundedAmount)}`}
            prefix={<DollarOutlined />}
            valueStyle={{ color: '#f5222d' }}
          />
        </Card>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Display impersonation banner if needed */}
       
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Payment Management</h1>
        <Space>
          <Dropdown overlay={actionsMenu}>
            <Button icon={<MoreOutlined />}>Actions</Button>
          </Dropdown>
        </Space>
      </div>

      {/* Payment Status Tabs */}
      <Card className="shadow-md mb-6">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="mb-4"
        >
          <Tabs.TabPane tab="All Orders" key="all" />
          <Tabs.TabPane 
            tab={
              <span>
                Unpaid <Badge count={orders.filter(o => parseInt(o.payment_status) === 0).length} style={{ backgroundColor: '#faad14' }} />
              </span>
            } 
            key="unpaid" 
          />
          <Tabs.TabPane 
            tab={
              <span>
                Paid <Badge count={orders.filter(o => parseInt(o.payment_status) === 1).length} style={{ backgroundColor: '#52c41a' }} />
              </span>
            } 
            key="paid" 
          />
          <Tabs.TabPane 
            tab={
              <span>
                Refunded <Badge count={orders.filter(o => parseInt(o.payment_status) === 2 || parseInt(o.payment_status) === 3).length} style={{ backgroundColor: '#f5222d' }} />
              </span>
            } 
            key="refunded" 
          />
        </Tabs>
        
        {/* Payment Metrics Cards */}
        {renderPaymentMetrics()}
      </Card>

      <Card className="shadow-md mb-6">
        {/* Date Navigation and Filter Controls */}
        <div className="flex flex-wrap justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <DateRangeSelector
              dateViewMode={dateViewMode}
              currentDate={currentDate}
              dateDisplayText={dateDisplayText}
              isCustomRange={isCustomRange}
              onDateRangeChange={handleDateRangeChange}
              onCustomRangeChange={handleCustomRangeChange}
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <EnhancedSearchComponent
              onSearch={handleSearch}
              getData={handleResetFilters}
              initialValue={searchText}
              initialCategory={searchCategory}
              onDateRangeChange={handleDateSearchChange}
            />

            {/* Client Selection Dropdown */}
            <Select
              placeholder="Select Client"
              allowClear
              showSearch
              style={{ width: 260 }}
              value={selectedClient}
              onChange={handleClientChange}
              loading={clientsLoading}
              optionFilterProp="search"
              optionRender={(option) => <ClientOptionLabel client={option?.data?.client || option?.data} />}
              options={(clients || []).map((client) => ({
                value: client.id,
                label: getClientSelectedLabel(client),
                client,
                search: buildClientSearchText(client),
              }))}
            />

            <Select
              placeholder="Order Status"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => handleFilterChange('order_status', value)}
              value={filters.order_status}
            >
              <Option value="Scheduled">Scheduled</Option>
              <Option value="En-route">En-Route</Option>
              <Option value="Completed">Completed</Option>
              <Option value="Collected">Collected</Option>
              <Option value="Cancelled">Cancelled</Option>
              <Option value="Failed">Failed</Option>
            </Select>

            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFilters}
              className="flex items-center"
            >
              Reset
            </Button>

            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              className="flex items-center"
            >
              Export
            </Button>

            <Button
              type="primary"
              onClick={fetchOrders}
              icon={<ReloadOutlined />}
              className="bg-golden-navy"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* View Toggle and Group By Toggle */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <Segmented
              options={[
                {
                  value: 'table',
                  icon: <TableOutlined />,
                  label: 'Table'
                },
                {
                  value: 'card',
                  icon: <AppstoreOutlined />,
                  label: 'Card'
                }
              ]}
              value={viewType}
              onChange={setViewType}
            />
          </div>
          
          <div className="flex items-center">
            <span className="mr-2">Group by Date</span>
            <Switch 
              checked={groupByDate} 
              onChange={setGroupByDate}
            />
          </div>
        </div>

        {/* Bulk action section */}
        {bulkActionVisible && viewType === 'table' && (
          <div className="bg-gray-100 p-4 rounded-lg mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <span className="font-medium mr-2">Bulk Payment Update:</span>
              <span className="mr-2">{selectedRowKeys.length} orders selected</span>
              <Select
                placeholder="Select payment status"
                style={{ width: 150 }}
                value={selectedPaymentStatus}
                onChange={setSelectedPaymentStatus}
              >
                <Option value="0">Unpaid</Option>
                <Option value="1">Paid</Option>
                <Option value="2">Refunded</Option>
                <Option value="3">Partial Refund</Option>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleBulkMarkAsPaid}
                className="bg-green-600"
              >
                Mark Paid
              </Button>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={handleBulkPaymentStatusChange}
                className="bg-golden-navy"
              >
                Apply Status
              </Button>
              <Button
                onClick={() => {
                  setSelectedRowKeys([]);
                  setBulkActionVisible(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Table View */}
        {viewType === 'table' && !groupByDate && (
          <MyTable
            rowSelection={rowSelection}
            columns={tableColumns}
            dataSource={orders}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} orders`,
              pageSizeOptions: ['10', '25', '50', '100']
            }}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            size="middle"
            locale={{ emptyText: 'No orders found' }}
          />
        )}
        
        {/* Grouped Table View */}
        {viewType === 'table' && groupByDate && (
          <div>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Spin size="large" />
              </div>
            ) : (
              <>
                {getOrdersGroupedByDate().map(group => (
                  <div key={group.date} className="mb-8">
                    <h2 className="text-xl font-bold mb-4 pb-2 border-b flex items-center">
                      <CalendarOutlined className="mr-2" />
                      {group.displayDate} ({group.orders.length})
                    </h2>
                    
                    <Table
                      rowSelection={rowSelection}
                      columns={tableColumns}
                      dataSource={group.orders}
                      rowKey="id"
                      pagination={false}
                      scroll={{ x: 1200 }}
                      size="middle"
                      locale={{ emptyText: 'No orders found' }}
                    />
                  </div>
                ))}
                
                {/* Pagination for grouped table view */}
                <div className="mt-6 flex justify-center">
                  {pagination.total > pagination.pageSize && (
                    <div className="flex justify-center mt-4">
                      <Button
                        disabled={pagination.current === 1}
                        onClick={() => {
                          setPagination({
                            ...pagination,
                            current: pagination.current - 1
                          });
                        }}
                      >
                        Previous
                      </Button>
                      <div className="mx-4 flex items-center">
                        Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
                      </div>
                      <Button
                        disabled={pagination.current === Math.ceil(pagination.total / pagination.pageSize)}
                        onClick={() => {
                          setPagination({
                            ...pagination,
                            current: pagination.current + 1
                          });
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Card View */}
        {viewType === 'card' && (
          <div>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Spin size="large" />
              </div>
            ) : (
              <>
                {/* Grouped Cards View */}
                {groupByDate ? (
                  renderGroupedOrderCards()
                ) : (
                  /* Non-grouped Cards */
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {orders.map(order => renderOrderCard(order))}
                  </div>
                )}

                {/* Pagination for card view */}
                <div className="mt-6 flex justify-center">
                  {pagination.total > pagination.pageSize && (
                    <div className="flex justify-center mt-4">
                      <Button
                        disabled={pagination.current === 1}
                        onClick={() => {
                          setPagination({
                            ...pagination,
                            current: pagination.current - 1
                          });
                        }}
                      >
                        Previous
                      </Button>
                      <div className="mx-4 flex items-center">
                        Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
                      </div>
                      <Button
                        disabled={pagination.current === Math.ceil(pagination.total / pagination.pageSize)}
                        onClick={() => {
                          setPagination({
                            ...pagination,
                            current: pagination.current + 1
                          });
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </Card>
      <ViewModal
        visible={viewOrderId !== null}
        orderId={viewOrderId}
        onCancel={() => setViewOrderId(null)}
        users={clients}
        onCaptureSavedCard={handleCaptureSavedCard}
      />
    </div>
  );
};

export default SimplifiedPaymentManagement;
